import React, { useState, useMemo } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import {
  Search, Filter, History, Settings, Edit2, Trash2, Shield, ShieldOff,
  Check, X, RotateCcw, AlertTriangle, ChevronRight, ChevronDown, Layers,
} from 'lucide-react';

const QUALITY_TAGS = ['New', 'Moderate', 'Low', 'High', 'V Low', 'VV Low', 'Unknown'];

const QUALITY_COLORS = {
  'New': 'bg-purple-100 text-purple-800',
  'High': 'bg-green-100 text-green-800',
  'Moderate': 'bg-blue-100 text-blue-800',
  'Low': 'bg-yellow-100 text-yellow-800',
  'V Low': 'bg-orange-100 text-orange-800',
  'VV Low': 'bg-red-100 text-red-800',
  'Unknown': 'bg-gray-100 text-gray-800',
};

const formatValue = (v) => {
  if (!v) return '₹0';
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)} Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(2)} L`;
  return `₹${Math.round(v).toLocaleString('en-IN')}`;
};

const AllHoldingsView = ({ holdings = [], members = [], onRefresh }) => {
  const memberMap = useMemo(() => Object.fromEntries(members.map(m => [m.id, m])), [members]);

  // Filter / sort state
  const [statusFilter, setStatusFilter] = useState('all');
  const [memberFilter, setMemberFilter] = useState('all');
  const [qualityFilter, setQualityFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('symbol');
  const [sortDir, setSortDir] = useState('asc');
  const [showAll, setShowAll] = useState(false);
  const [groupBySymbol, setGroupBySymbol] = useState(false);
  const [expandedSymbols, setExpandedSymbols] = useState(new Set());

  // Edit / action state
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Pledge / unpledge
  const [showPledge, setShowPledge] = useState(false);
  const [pledgeForm, setPledgeForm] = useState({});
  const [lasAccounts, setLasAccounts] = useState([]);
  const [showUnpledge, setShowUnpledge] = useState(false);
  const [unpledgeForm, setUnpledgeForm] = useState({});

  // History
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);

  // Confirm
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState({ type: '', data: null, message: '' });

  const refresh = () => onRefresh && onRefresh();

  // ===== Filter helpers =====
  const qualityTagOptions = useMemo(() => {
    const tags = new Set(holdings.map(h => h.custom_fields?.category || 'Unknown'));
    return ['all', ...Array.from(tags)];
  }, [holdings]);

  const visible = useMemo(() => {
    let list = holdings.filter(h => !h.is_unlisted || h.is_private);
    if (statusFilter === 'pledged') list = list.filter(h => h.pledged);
    else if (statusFilter === 'free') list = list.filter(h => !h.pledged);
    if (memberFilter !== 'all') list = list.filter(h => h.member_id === memberFilter);
    if (qualityFilter !== 'all') list = list.filter(h => (h.custom_fields?.category || 'Unknown') === qualityFilter);
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(h => h.symbol?.toLowerCase().includes(s) || h.display_name?.toLowerCase().includes(s));
    }
    list.sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      let av, bv;
      switch (sortBy) {
        case 'symbol': return dir * (a.symbol || '').localeCompare(b.symbol || '');
        case 'member':
          return dir * (memberMap[a.member_id]?.name || '').localeCompare(memberMap[b.member_id]?.name || '');
        case 'quantity': av = a.quantity; bv = b.quantity; break;
        case 'price': av = a.purchase_price; bv = b.purchase_price; break;
        case 'value':
          av = (a.custom_fields?.current_price || a.purchase_price) * a.quantity;
          bv = (b.custom_fields?.current_price || b.purchase_price) * b.quantity;
          break;
        case 'status': av = a.pledged ? 1 : 0; bv = b.pledged ? 1 : 0; break;
        default: return 0;
      }
      return dir * (av - bv);
    });
    return list;
  }, [holdings, statusFilter, memberFilter, qualityFilter, search, sortBy, sortDir, memberMap]);

  const summary = useMemo(() => {
    const total = visible.reduce((s, h) => s + (h.custom_fields?.current_price || h.purchase_price) * h.quantity, 0);
    const totalQty = visible.reduce((s, h) => s + h.quantity, 0);
    const pledged = visible.filter(h => h.pledged);
    const free = visible.filter(h => !h.pledged);
    const pledgedVal = pledged.reduce((s, h) => s + (h.custom_fields?.current_price || h.purchase_price) * h.quantity, 0);
    const freeVal = free.reduce((s, h) => s + (h.custom_fields?.current_price || h.purchase_price) * h.quantity, 0);
    return { total, totalQty, pledgedCount: pledged.length, freeCount: free.length, pledgedVal, freeVal, count: visible.length };
  }, [visible]);

  // Grouped data: by symbol (used when groupBySymbol === true)
  const grouped = useMemo(() => {
    if (!groupBySymbol) return [];
    const map = new Map();
    visible.forEach(h => {
      const key = h.symbol;
      if (!map.has(key)) {
        map.set(key, {
          symbol: key,
          display_name: h.display_name || h.symbol,
          ticker_symbol: h.ticker_symbol,
          currentPrice: h.custom_fields?.current_price || h.purchase_price,
          totalQty: 0,
          totalValue: 0,
          totalCost: 0,
          pledgedCount: 0,
          freeCount: 0,
          memberCounts: {},
          lots: [],
        });
      }
      const g = map.get(key);
      const px = h.custom_fields?.current_price || h.purchase_price;
      g.totalQty += h.quantity;
      g.totalValue += px * h.quantity;
      g.totalCost += h.purchase_price * h.quantity;
      if (h.pledged) g.pledgedCount += 1;
      else g.freeCount += 1;
      const mn = memberMap[h.member_id]?.name || 'Unknown';
      g.memberCounts[mn] = (g.memberCounts[mn] || 0) + h.quantity;
      g.lots.push(h);
    });
    let arr = Array.from(map.values());
    arr.sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      switch (sortBy) {
        case 'symbol': return dir * a.symbol.localeCompare(b.symbol);
        case 'quantity': return dir * (a.totalQty - b.totalQty);
        case 'value': return dir * (a.totalValue - b.totalValue);
        default: return dir * a.symbol.localeCompare(b.symbol);
      }
    });
    return arr;
  }, [visible, groupBySymbol, sortBy, sortDir, memberMap]);

  const toggleSymbolExpanded = (symbol) => {
    const next = new Set(expandedSymbols);
    if (next.has(symbol)) next.delete(symbol);
    else next.add(symbol);
    setExpandedSymbols(next);
  };

  // ===== Action handlers =====
  const startEdit = (h) => {
    setEditingId(h.id);
    setEditForm({
      symbol: h.symbol,
      quantity: h.quantity,
      purchase_type: h.purchase_type || 'Primary',
      purchase_price: h.purchase_price,
      quality_tag: h.custom_fields?.category || 'Unknown',
    });
  };

  const saveEdit = async (id) => {
    try {
      await api.put(`/holdings/${id}`, {
        ...editForm,
        custom_fields: { category: editForm.quality_tag },
      });
      toast.success('Holding updated');
      setEditingId(null);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  const askDelete = (h) => {
    setConfirmAction({
      type: 'delete', data: h.id,
      message: `Are you sure you want to delete the holding "${h.symbol}" (${h.quantity.toLocaleString('en-IN')} shares)?\n\nThis will be recorded in history and can be rolled back.`,
    });
    setShowConfirm(true);
  };

  const askRollback = (id, desc) => {
    setConfirmAction({
      type: 'rollback', data: id,
      message: `Rollback this change?\n\n${desc || ''}`,
    });
    setShowConfirm(true);
  };

  const doConfirm = async () => {
    const { type, data } = confirmAction;
    setShowConfirm(false);
    try {
      if (type === 'delete') {
        await api.delete(`/holdings/${data}`);
        toast.success('Holding deleted');
        refresh();
      } else if (type === 'rollback') {
        const r = await api.post(`/change-history/${data}/rollback`);
        toast.success(r.data?.message || 'Rolled back');
        loadHistory();
        refresh();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
  };

  const openPledge = async (h) => {
    try {
      const r = await api.get('/las-accounts');
      if (!r.data?.length) { toast.error('Please create a LAS account first'); return; }
      setLasAccounts(r.data);
      setPledgeForm({ holdingId: h.id, symbol: h.symbol, quantity: h.quantity, lasAccountId: r.data[0].id, pledgeQuantity: h.quantity });
      setShowPledge(true);
    } catch (e) { toast.error('Failed to load LAS accounts'); }
  };

  const doPledge = async () => {
    try {
      await api.post(`/holdings/${pledgeForm.holdingId}/pledge`, {
        las_account_id: pledgeForm.lasAccountId,
        quantity: pledgeForm.pledgeQuantity,
      });
      toast.success(`Pledged ${pledgeForm.pledgeQuantity} shares`);
      setShowPledge(false);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to pledge'); }
  };

  const openUnpledge = (h) => {
    setUnpledgeForm({ holdingId: h.id, symbol: h.symbol, quantity: h.quantity, unpledgeQuantity: h.quantity });
    setShowUnpledge(true);
  };

  const doUnpledge = async () => {
    try {
      await api.post(`/holdings/${unpledgeForm.holdingId}/unpledge`, { quantity: unpledgeForm.unpledgeQuantity });
      toast.success(`Unpledged ${unpledgeForm.unpledgeQuantity} shares`);
      setShowUnpledge(false);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to unpledge'); }
  };

  const loadHistory = async () => {
    try {
      const r = await api.get('/change-history?entity_type=holding&limit=50');
      setHistory(r.data || []);
      setShowHistory(true);
    } catch (e) { toast.error('Failed to load history'); }
  };

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(key); setSortDir('asc'); }
  };

  const sortIcon = (key) => sortBy === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';
  const limit = showAll ? visible.length : 100;

  return (
    <div className="space-y-4" data-testid="all-holdings-view">
      {/* Header: compact summary bar (streamlined to match Stocks tab style) */}
      <div className="flex flex-wrap items-center justify-between gap-3 pb-2 border-b border-gray-100">
        <div className="flex items-center flex-wrap gap-x-5 gap-y-1 text-sm">
          <span className="text-gray-500">HOLDINGS: <span className="font-bold text-indigo-700">{summary.count}</span></span>
          <span className="text-gray-300">|</span>
          <span className="text-gray-500">VALUE: <span className="font-bold text-green-700">{formatValue(summary.total)}</span></span>
          <span className="text-gray-300">|</span>
          <span className="text-gray-500">QTY: <span className="font-bold text-gray-700">{summary.totalQty.toLocaleString('en-IN')}</span></span>
          <span className="text-gray-300">|</span>
          <span className="text-gray-500">PLEDGED: <span className="font-bold text-amber-700">{summary.pledgedCount}</span> <span className="text-xs text-gray-400">({formatValue(summary.pledgedVal)})</span></span>
          <span className="text-gray-300">|</span>
          <span className="text-gray-500">FREE: <span className="font-bold text-green-700">{summary.freeCount}</span> <span className="text-xs text-gray-400">({formatValue(summary.freeVal)})</span></span>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setGroupBySymbol(!groupBySymbol)}
            variant="outline" size="sm"
            className={groupBySymbol ? 'bg-indigo-50 border-indigo-500 text-indigo-700' : ''}
            data-testid="group-by-symbol-btn"
          >
            <Layers size={14} className="mr-1" /> {groupBySymbol ? 'Ungroup' : 'Group by Symbol'}
          </Button>
          <Button onClick={loadHistory} variant="outline" size="sm" data-testid="holdings-history-btn">
            <History size={14} className="mr-1" /> History
          </Button>
          <Button
            onClick={() => { setEditMode(!editMode); if (editingId) setEditingId(null); }}
            variant="outline" size="sm"
            className={editMode ? 'bg-amber-50 border-amber-500 text-amber-700' : ''}
            data-testid="holdings-edit-mode-btn"
          >
            <Settings size={14} className="mr-1" /> {editMode ? 'Exit Edit' : 'Edit Mode'}
          </Button>
        </div>
      </div>

      {editMode && (
        <div className="p-2 bg-amber-50 border border-amber-200 rounded text-amber-800 text-xs">
          ⚠️ Edit Mode is ON — you can edit, pledge/unpledge, or delete holdings.
        </div>
      )}

      {/* Filters */}
      <div className="card p-3 flex flex-wrap items-center gap-3">
        <Filter size={16} className="text-gray-400" />
        <select className="input-field text-sm py-1" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="filter-status">
          <option value="all">All Status</option>
          <option value="pledged">Pledged</option>
          <option value="free">Free</option>
        </select>
        <select className="input-field text-sm py-1" value={memberFilter} onChange={e => setMemberFilter(e.target.value)} data-testid="filter-member">
          <option value="all">All Members</option>
          {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
        <select className="input-field text-sm py-1" value={qualityFilter} onChange={e => setQualityFilter(e.target.value)} data-testid="filter-quality">
          {qualityTagOptions.map(t => <option key={t} value={t}>{t === 'all' ? 'All Quality' : t}</option>)}
        </select>
        <div className="flex items-center gap-2 ml-auto">
          <Search size={16} className="text-gray-400" />
          <input
            type="text" placeholder="Search symbol..." value={search}
            onChange={e => setSearch(e.target.value)}
            className="input-field text-sm py-1 w-40" data-testid="search-symbol"
          />
        </div>
      </div>

      {/* Holdings table */}
      <div className="card overflow-hidden">
        <div className="overflow-auto" style={{ maxHeight: '65vh' }}>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
            <tr>
              {groupBySymbol && <th className="w-8"></th>}
              <th className="text-left py-2 px-3 font-semibold cursor-pointer hover:bg-gray-100" onClick={() => toggleSort('symbol')}>Symbol{sortIcon('symbol')}</th>
              {!groupBySymbol && <th className="text-left py-2 px-3 font-semibold cursor-pointer hover:bg-gray-100" onClick={() => toggleSort('member')}>Member{sortIcon('member')}</th>}
              {groupBySymbol && <th className="text-left py-2 px-3 font-semibold">Members</th>}
              <th className="text-right py-2 px-3 font-semibold cursor-pointer hover:bg-gray-100" onClick={() => toggleSort('quantity')}>{groupBySymbol ? 'Total Qty' : 'Qty'}{sortIcon('quantity')}</th>
              {!groupBySymbol && <th className="text-left py-2 px-3 font-semibold">Type</th>}
              {!groupBySymbol && <th className="text-right py-2 px-3 font-semibold cursor-pointer hover:bg-gray-100" onClick={() => toggleSort('price')}>Avg Price{sortIcon('price')}</th>}
              <th className="text-right py-2 px-3 font-semibold">Current</th>
              <th className="text-right py-2 px-3 font-semibold cursor-pointer hover:bg-gray-100" onClick={() => toggleSort('value')}>Value{sortIcon('value')}</th>
              {!groupBySymbol && <th className="text-center py-2 px-3 font-semibold">Quality</th>}
              {!groupBySymbol && <th className="text-center py-2 px-3 font-semibold cursor-pointer hover:bg-gray-100" onClick={() => toggleSort('status')}>Status{sortIcon('status')}</th>}
              {groupBySymbol && <th className="text-center py-2 px-3 font-semibold">Lots</th>}
              {editMode && !groupBySymbol && <th className="text-center py-2 px-3 font-semibold">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {groupBySymbol ? (
              <>
                {grouped.length === 0 && (
                  <tr><td colSpan={7} className="py-8 text-center text-gray-500">No holdings match your filters.</td></tr>
                )}
                {grouped.map((g, i) => {
                  const isExpanded = expandedSymbols.has(g.symbol);
                  const memberSummary = Object.entries(g.memberCounts)
                    .sort((a, b) => b[1] - a[1])
                    .map(([n, q]) => `${n} (${q.toLocaleString('en-IN')})`)
                    .join(', ');
                  return (
                    <React.Fragment key={g.symbol}>
                      <tr
                        className={`border-b cursor-pointer hover:bg-indigo-50/40 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'} ${isExpanded ? 'bg-indigo-50/30' : ''}`}
                        onClick={() => toggleSymbolExpanded(g.symbol)}
                        data-testid={`group-row-${g.symbol}`}
                      >
                        <td className="py-2 px-2 text-center">
                          {isExpanded ? <ChevronDown size={16} className="text-indigo-600" /> : <ChevronRight size={16} className="text-gray-400" />}
                        </td>
                        <td className="py-2 px-3">
                          <div className="font-medium">{g.display_name}</div>
                          {g.ticker_symbol && <div className="text-xs text-gray-400">{g.ticker_symbol}</div>}
                        </td>
                        <td className="py-2 px-3 text-sm text-gray-600 max-w-md truncate" title={memberSummary}>{memberSummary}</td>
                        <td className="py-2 px-3 text-right font-medium">{g.totalQty.toLocaleString('en-IN')}</td>
                        <td className="py-2 px-3 text-right">₹{g.currentPrice?.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                        <td className="py-2 px-3 text-right font-semibold text-primary">{formatValue(g.totalValue)}</td>
                        <td className="py-2 px-3 text-center text-sm">
                          <span className="text-gray-600">{g.lots.length}</span>
                          {g.pledgedCount > 0 && <span className="ml-1 text-xs text-amber-600">({g.pledgedCount} pl)</span>}
                        </td>
                      </tr>
                      {isExpanded && g.lots.map(h => {
                        const member = memberMap[h.member_id];
                        const px = h.custom_fields?.current_price || h.purchase_price;
                        const tag = h.custom_fields?.category || 'Unknown';
                        return (
                          <tr key={h.id} className="border-b bg-indigo-50/10 text-xs" data-testid={`group-lot-${h.id}`}>
                            <td></td>
                            <td className="py-1.5 px-3 pl-8 text-gray-500">↳ Lot</td>
                            <td className="py-1.5 px-3">{member?.name || 'Unknown'}</td>
                            <td className="py-1.5 px-3 text-right">{h.quantity.toLocaleString('en-IN')}</td>
                            <td className="py-1.5 px-3 text-right text-gray-500">Buy ₹{h.purchase_price?.toFixed(2)}</td>
                            <td className="py-1.5 px-3 text-right">{formatValue(px * h.quantity)}</td>
                            <td className="py-1.5 px-3 text-center">
                              <span className={`px-1.5 py-0.5 rounded text-[10px] ${QUALITY_COLORS[tag] || QUALITY_COLORS.Unknown}`}>{tag}</span>
                              <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] ${h.pledged ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`}>
                                {h.pledged ? 'P' : 'F'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </>
            ) : (
              <>
            {visible.slice(0, limit).map((h, i) => {
              const member = memberMap[h.member_id];
              const currentPrice = h.custom_fields?.current_price || h.purchase_price;
              const value = currentPrice * h.quantity;
              const tag = h.custom_fields?.category || 'Unknown';
              const isEditing = editingId === h.id;

              if (isEditing) {
                return (
                  <tr key={h.id} className="border-b bg-yellow-50">
                    <td colSpan={editMode ? 10 : 9} className="py-3 px-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <input className="input-field w-24 text-sm py-1" value={editForm.symbol} onChange={e => setEditForm({ ...editForm, symbol: e.target.value.toUpperCase() })} data-testid="edit-symbol" />
                        <input type="number" className="input-field w-24 text-sm py-1" value={editForm.quantity} onChange={e => setEditForm({ ...editForm, quantity: parseInt(e.target.value) || 0 })} data-testid="edit-qty" />
                        <select className="input-field w-24 text-sm py-1" value={editForm.purchase_type} onChange={e => setEditForm({ ...editForm, purchase_type: e.target.value })} data-testid="edit-type">
                          <option value="Legacy">Legacy</option>
                          <option value="Primary">Primary</option>
                          <option value="MTF">MTF</option>
                        </select>
                        <select className="input-field w-24 text-sm py-1" value={editForm.quality_tag} onChange={e => setEditForm({ ...editForm, quality_tag: e.target.value })} data-testid="edit-quality">
                          {QUALITY_TAGS.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <input type="number" step="0.01" className="input-field w-28 text-sm py-1" value={editForm.purchase_price} onChange={e => setEditForm({ ...editForm, purchase_price: parseFloat(e.target.value) || 0 })} data-testid="edit-price" />
                        <Button onClick={() => saveEdit(h.id)} className="btn-primary px-2 py-1" size="sm" data-testid="edit-save"><Check size={14} /></Button>
                        <Button onClick={() => setEditingId(null)} variant="outline" className="px-2 py-1" size="sm" data-testid="edit-cancel"><X size={14} /></Button>
                      </div>
                    </td>
                  </tr>
                );
              }

              return (
                <tr key={h.id} className={`border-b hover:bg-gray-50 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                  <td className="py-2 px-3">
                    <div className="font-medium">{h.display_name || h.symbol}</div>
                    {h.ticker_symbol && <div className="text-xs text-gray-400">{h.ticker_symbol}</div>}
                  </td>
                  <td className="py-2 px-3">
                    <div>{member?.name || 'Unknown'}</div>
                    <div className="text-xs text-gray-400">{member?.relation || ''}</div>
                  </td>
                  <td className="py-2 px-3 text-right">{h.quantity.toLocaleString('en-IN')}</td>
                  <td className="py-2 px-3"><span className="badge badge-success text-xs">{h.purchase_type}</span></td>
                  <td className="py-2 px-3 text-right">₹{h.purchase_price?.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                  <td className="py-2 px-3 text-right">₹{currentPrice?.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                  <td className="py-2 px-3 text-right font-medium text-primary">{formatValue(value)}</td>
                  <td className="py-2 px-3 text-center">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${QUALITY_COLORS[tag] || QUALITY_COLORS.Unknown}`}>{tag}</span>
                  </td>
                  <td className="py-2 px-3 text-center">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${h.pledged ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`}>
                      {h.pledged ? 'Pledged' : 'Free'}
                    </span>
                  </td>
                  {editMode && (
                    <td className="py-2 px-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {!h.pledged ? (
                          <button onClick={() => startEdit(h)} className="p-1 hover:bg-blue-50 rounded text-blue-600" title="Edit" data-testid={`edit-btn-${h.id}`}><Edit2 size={15} /></button>
                        ) : (
                          <button disabled className="p-1 rounded text-gray-300 cursor-not-allowed" title="Cannot edit pledged"><Edit2 size={15} /></button>
                        )}
                        <button onClick={() => h.pledged ? openUnpledge(h) : openPledge(h)} className="p-1 hover:bg-blue-50 rounded text-blue-600" title={h.pledged ? 'Unpledge' : 'Pledge'} data-testid={`pledge-btn-${h.id}`}>
                          {h.pledged ? <ShieldOff size={15} /> : <Shield size={15} />}
                        </button>
                        {!h.pledged ? (
                          <button onClick={() => askDelete(h)} className="p-1 hover:bg-red-50 rounded text-red-600" title="Delete" data-testid={`delete-btn-${h.id}`}><Trash2 size={15} /></button>
                        ) : (
                          <button disabled className="p-1 rounded text-gray-300 cursor-not-allowed" title="Cannot delete pledged"><Trash2 size={15} /></button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
            {visible.length === 0 && (
              <tr><td colSpan={editMode ? 10 : 9} className="py-8 text-center text-gray-500">No holdings match your filters.</td></tr>
            )}
              </>
            )}
          </tbody>
        </table>
        </div>
        {visible.length > limit && (
          <div className="text-center py-3 border-t">
            <button onClick={() => setShowAll(true)} className="text-sm text-primary hover:underline" data-testid="show-all-btn">
              Show all {visible.length} holdings (currently showing {limit})
            </button>
          </div>
        )}
        {visible.length > 0 && showAll && (
          <div className="text-center py-2 border-t text-xs text-gray-500">Showing all {visible.length} holdings</div>
        )}
      </div>

      {/* Pledge dialog */}
      <Dialog open={showPledge} onOpenChange={setShowPledge}>
        <DialogContent className="max-w-md" data-testid="pledge-dialog">
          <DialogHeader><DialogTitle>Pledge Shares: {pledgeForm.symbol}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium mb-1 block">LAS Account</label>
              <select className="input-field w-full" value={pledgeForm.lasAccountId} onChange={e => setPledgeForm({ ...pledgeForm, lasAccountId: e.target.value })}>
                {lasAccounts.map(a => <option key={a.id} value={a.id}>{a.bank_name} - {a.account_number}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Quantity to Pledge (max: {pledgeForm.quantity})</label>
              <input type="number" className="input-field w-full" min="1" max={pledgeForm.quantity}
                value={pledgeForm.pledgeQuantity || 0}
                onChange={e => setPledgeForm({ ...pledgeForm, pledgeQuantity: parseInt(e.target.value) || 0 })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPledge(false)}>Cancel</Button>
            <Button onClick={doPledge} className="btn-primary" data-testid="pledge-confirm">Pledge</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unpledge dialog */}
      <Dialog open={showUnpledge} onOpenChange={setShowUnpledge}>
        <DialogContent className="max-w-md" data-testid="unpledge-dialog">
          <DialogHeader><DialogTitle>Unpledge Shares: {unpledgeForm.symbol}</DialogTitle></DialogHeader>
          <div className="py-2">
            <label className="text-sm font-medium mb-1 block">Quantity to Unpledge (max: {unpledgeForm.quantity})</label>
            <input type="number" className="input-field w-full" min="1" max={unpledgeForm.quantity}
              value={unpledgeForm.unpledgeQuantity || 0}
              onChange={e => setUnpledgeForm({ ...unpledgeForm, unpledgeQuantity: parseInt(e.target.value) || 0 })} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUnpledge(false)}>Cancel</Button>
            <Button onClick={doUnpledge} className="btn-primary" data-testid="unpledge-confirm">Unpledge</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History modal */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="history-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><History size={20} /> Change History</DialogTitle></DialogHeader>
          {history.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No changes recorded yet.</div>
          ) : (
            <div className="space-y-3">
              {history.map(item => {
                const prev = item.previous_data || {};
                const newData = item.new_data || {};
                const isDelete = item.action === 'delete';
                return (
                  <div key={item.id} className={`p-3 rounded-lg border ${isDelete ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`}>
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${isDelete ? 'bg-red-200 text-red-800' : 'bg-blue-200 text-blue-800'}`}>
                            {item.action?.toUpperCase()}
                          </span>
                          <span className="text-xs text-gray-500">{new Date(item.created_at).toLocaleString('en-IN')}</span>
                        </div>
                        <div className="font-medium mb-1">{prev.symbol} × {prev.quantity?.toLocaleString('en-IN')} shares</div>
                        <p className="text-xs text-gray-600 italic">{item.description}</p>
                      </div>
                      <Button onClick={() => askRollback(item.id, item.description)} variant="outline" size="sm" className="whitespace-nowrap">
                        <RotateCcw size={14} className="mr-1" /> Rollback
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm dialog */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="max-w-md" data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="text-amber-500" size={22} /> Confirm Action
            </DialogTitle>
          </DialogHeader>
          <p className="py-3 whitespace-pre-line text-gray-700">{confirmAction.message}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)}>Cancel</Button>
            <Button
              onClick={doConfirm}
              className={confirmAction.type === 'delete' ? 'bg-red-600 hover:bg-red-700 text-white' : 'btn-primary'}
              data-testid="confirm-yes"
            >
              {confirmAction.type === 'rollback' ? 'Yes, Rollback' : 'Yes, Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AllHoldingsView;
